import { type TokenPrecision } from 'src/services/json-api/json-api.types'
import {
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
} from 'src/tpbp-core/models/common.const'

const appContext = {
  accessToken: '',
  apiUrl: '',
  hasAdvisingReportAuthToken: false,
}

type ContextKeys = keyof typeof appContext

function checkContextProperty(key) {
  if (!Object.hasOwn(appContext, key)) {
    throw new Error(`Unknown context '${key}'`)
  }
}

function setContextValue<T extends ContextKeys>(
  key: T,
  value: (typeof appContext)[T],
) {
  checkContextProperty(key)
  appContext[key] = value
}

function getContextValue<T extends ContextKeys>(key: T) {
  checkContextProperty(key)
  return appContext[key]
}

export function setPrecisionsInSessionStorage(
  precisions: string[] | TokenPrecision[],
) {
  sessionStorage.setItem(
    SESSION_STORAGE_KEYS.USER_PRECISIONS,
    JSON.stringify(precisions || []),
  )
}

export function getPrecisionForPdfTabIsolation(): TokenPrecision[] {
  return JSON.parse(
    sessionStorage.getItem(SESSION_STORAGE_KEYS.USER_PRECISIONS) ?? '[]',
  ) as TokenPrecision[]
}

export function setAccessToken(
  accessToken: string,
  hasAdvisingReportAuthToken = false,
) {
  if (hasAdvisingReportAuthToken) {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.ACCESS_TOKEN, accessToken)
  } else {
    // !need to store in localStorage only because application reloads after log-in
    // TODO change the way application loads data after log-in to avoid this storage
    localStorage.setItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN, accessToken)
  }
  setContextValue('accessToken', accessToken)
  setContextValue('hasAdvisingReportAuthToken', hasAdvisingReportAuthToken)
}

export function setApiUrl(apiUrl: string) {
  setContextValue('apiUrl', apiUrl)
}

export function getAccessToken() {
  const accessToken = getContextValue('accessToken')
  if (accessToken !== '') {
    return accessToken
  }
  const storedAccessToken =
    localStorage.getItem(LOCAL_STORAGE_KEYS.ACCESS_TOKEN) ||
    sessionStorage.getItem(SESSION_STORAGE_KEYS.ACCESS_TOKEN)
  if (storedAccessToken) {
    setContextValue('accessToken', storedAccessToken)
  }
  return storedAccessToken || ''
}

export function getHasAdvisingReportAuthToken() {
  return getContextValue('hasAdvisingReportAuthToken')
}

export function getUrl(endpoint: string, useTpbpPrefix = false) {
  if (appContext.apiUrl === '') {
    throw new Error('appContext.apiUrl is not initialized')
  }
  if (endpoint.includes('://')) {
    throw new Error('Endpoint must not contain protocol "://"')
  }
  if (useTpbpPrefix && endpoint.startsWith('/tpbp/')) {
    throw new Error('Endpoint should not start with /tpbp/')
  }
  const url = `${appContext.apiUrl}/${useTpbpPrefix ? 'tpbp' : ''}/${endpoint}`
  // replace multiple slashes with one expect for protocol
  return url.replace(/(?<!:)\/+/g, '/')
}
